import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import mentorshipService from '@/services/mentorshipService';

const SubscriptionIdModal = ({ open, onOpenChange, batch }) => {
    const [subscriptionId, setSubscriptionId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        if (open && batch) {
            // Initialize with existing subscription ID if available
            setSubscriptionId(batch.subscriptionId || '');
        }
    }, [open, batch]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!batch) return;
        
        // Validate subscription ID
        if (!subscriptionId.trim()) {
            setError('Subscription ID is required');
            return;
        }
        
        setIsLoading(true);
        setProgress('Updating subscription ID...');
        setError(null);
        
        try {
            // Call the API to update batch and students
            const response = await mentorshipService.updateBatchSubscription(
                batch.PK,
                batch.SK,
                subscriptionId
            );
            
            setProgress(`Successfully updated batch and ${response.studentsUpdated} students.`);
            
            if (response.studentsFailed > 0) {
                setError(`Failed to update ${response.studentsFailed} students.`);
            }
            
            toast.success('Subscription ID updated successfully');
            
            // Close modal after a short delay to show success message
            setTimeout(() => {
                onOpenChange(false);
            }, 2000);
            
        } catch (err) {
            console.error('Error updating subscription ID:', err);
            setError(err.response?.data?.error || err.message || 'Failed to update subscription ID');
            toast.error('Failed to update subscription ID');
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Update Subscription ID</DialogTitle>
                    <DialogDescription>
                        Update the Pabbly subscription ID for batch {batch?.SK}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="subscriptionId" className="text-right">
                                Subscription ID
                            </Label>
                            <Input
                                id="subscriptionId"
                                value={subscriptionId}
                                onChange={(e) => setSubscriptionId(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter Pabbly subscription list ID"
                                disabled={isLoading}
                            />
                        </div>
                        
                        {progress && (
                            <div className="bg-muted p-2 rounded text-sm">
                                {progress}
                            </div>
                        )}
                        
                        {error && (
                            <div className="bg-destructive/10 text-destructive p-2 rounded text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                'Update Subscription ID'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

SubscriptionIdModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    batch: PropTypes.object
};

export default SubscriptionIdModal; 